import { EllipsedText, FlexRow, MaterialIcon, useLoading, useModal, useMoment } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { ParagraphXSmall } from 'baseui/typography';
import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { ASSET_TYPE, ASSETS_TABLE_COLUMNS_NAME } from '../../constants/asset';
import { useStyles } from '../../hooks/useStyles';
import useUpdateEffect from '../../hooks/useUpdateEffect';
import { getAssets } from '../../services/assetService';
import { assetsTypesState, assetTypeState } from '../../storage/assets';
import { Asset, AssetType } from '../../types/asset';
import CommandModal from '../commands/CommandModal';
import { ListWrapper } from '../shared/ListWrapper';
import StyledTooltip from '../ui/StyledTooltip';
import AssetDetailModal from './modal/AssetDetailModal';

type Props = {
  selectedClientId?: string;
  selectedSubClientId?: string;
  search?: string;
  reloadPage?: boolean;
  monitoring?: boolean;
};

export function AssetList({ selectedClientId, selectedSubClientId, search, reloadPage, monitoring }: Readonly<Props>) {
  const [assets, setAssets] = useState<Asset[]>();
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const { loading, startLoading, stopLoading } = useLoading();
  const assetsTypes = useRecoilValue(assetsTypesState);
  const assetType = useRecoilValue(assetTypeState);
  const [selectedAsset, setSelectedAsset] = useState<Asset>();
  const { openModal, closeModal, showModal } = useModal();
  const { formatDateFromTs } = useMoment();
  const { theme } = useStyles();
  const { openModal: openCommandModal, closeModal: closeCommandModal, showModal: showCommandModal } = useModal();

  useUpdateEffect(() => {
    setPage(0);
  }, [reloadPage, assetType, selectedClientId, selectedSubClientId, search, monitoring]);

  useEffect(() => {
    if (page === 0) {
      setPage(1);
      return;
    }
    startLoading();
    const loadAssetsTableData = async () => {
      const response = await getAssets({
        assetType,
        page,
        clientId: selectedClientId,
        subClientId: selectedSubClientId,
        q: search,
        monitoring,
      });
      if (response?.error) {
        setAssets(undefined);
        setTotalItems(0);
      } else {
        setAssets(response?.assets);
        setTotalItems(response?.total ?? 0);
      }
      stopLoading();
    };
    loadAssetsTableData();
  }, [page]);

  const openAssetModal = (asset: Asset) => {
    setSelectedAsset(asset);
    openModal();
  };

  const closeAssetModal = (updateTable?: boolean) => {
    setSelectedAsset(undefined);
    closeModal();
    if (updateTable) setPage(0);
  };

  const onOpenCommandModal = (asset: Asset) => {
    setSelectedAsset(asset);
    openCommandModal();
  };

  const onCloseCommandModal = () => {
    setSelectedAsset(undefined);
    closeCommandModal();
  };

  const getAssetTypeLabel = (assetType?: AssetType) => {
    const label = assetsTypes?.find((type) => assetType === type.key)?.label;

    return label ?? (assetType !== undefined ? ASSET_TYPE[assetType] : '-');
  };

  return (
    <>
      <ListWrapper
        columns={ASSETS_TABLE_COLUMNS_NAME}
        data={
          assets?.map((asset, i) => {
            return {
              items: [
                <ParagraphXSmall
                  margin={0}
                  key={`asset-device-${i}`}
                >
                  <EllipsedText>{asset?.device?.ident ?? '-'}</EllipsedText>
                </ParagraphXSmall>,
                <ParagraphXSmall
                  margin={0}
                  key={`asset-type-${i}`}
                >
                  {getAssetTypeLabel(asset?.assetType)}
                </ParagraphXSmall>,
                <ParagraphXSmall
                  margin={0}
                  key={`asset-label-${i}`}
                >
                  <EllipsedText>{asset?.label ?? '-'}</EllipsedText>
                </ParagraphXSmall>,
                <ParagraphXSmall
                  margin={0}
                  key={`asset-client-${i}`}
                >
                  {asset?.client?.name ?? '-'}
                </ParagraphXSmall>,
                <ParagraphXSmall
                  margin={0}
                  key={`asset-subclient-${i}`}
                >
                  {asset?.subClient?.name ?? '-'}
                </ParagraphXSmall>,
                <ParagraphXSmall
                  margin={0}
                  key={`asset-cs-${i}`}
                >
                  {asset?.cs?.timestamp ? formatDateFromTs(asset.cs.timestamp) : '-'}
                </ParagraphXSmall>,
                <ParagraphXSmall
                  margin={0}
                  key={`asset-monitoring-${i}`}
                >
                  {asset?.monitoring ? 'Si' : 'No'}
                </ParagraphXSmall>,
                <FlexRow
                  key={`asset-actions-${i}`}
                  centered
                  gap={theme.sizing.scale100}
                >
                  {asset.device?.ident && (
                    <StyledTooltip
                      content="Enviar comando"
                      showArrow={false}
                      placement="bottom"
                      ignoreBoundary
                    >
                      <Button
                        size="compact"
                        shape="square"
                        kind="tertiary"
                        onClick={() => onOpenCommandModal(asset)}
                      >
                        <MaterialIcon name="settings_remote" />
                      </Button>
                    </StyledTooltip>
                  )}
                  <StyledTooltip
                    content={'Ver detalles'}
                    showArrow={false}
                  >
                    <Button
                      size="compact"
                      shape="square"
                      onClick={() => openAssetModal(asset)}
                      kind="tertiary"
                    >
                      <MaterialIcon name={'visibility'} />
                    </Button>
                  </StyledTooltip>
                </FlexRow>,
              ],
            };
          }) ?? []
        }
        hasData={assets !== undefined}
        isEmpty={!assets?.length}
        loading={loading}
        noDataDescription="Todavía no tienes ningun activo"
        onPageChange={setPage}
        page={page}
        totalItems={totalItems}
      />
      {showModal && (
        <AssetDetailModal
          onClose={(updateTable) => closeAssetModal(updateTable)}
          isOpen={showModal}
          selectedAssetId={selectedAsset?._id}
        />
      )}
      {selectedAsset && (
        <CommandModal
          onClose={onCloseCommandModal}
          isOpen={showCommandModal}
          asset={selectedAsset}
        />
      )}
    </>
  );
}
